import type { TFunction } from 'i18next';

import { useCallback } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { handleApiError } from '@/utils/errorUtils';

interface UseErrorHandlerReturn {
  handleError: (error: unknown) => string;
}

/**
 * Centralized error handling hook for consistent error management across the app
 */
export function useErrorHandler(
  namespace: string = 'common'
): UseErrorHandlerReturn {
  const { t } = useTranslation();

  const handleError = useCallback(
    (error: unknown): string => {
      return handleApiError(error, t as TFunction, namespace);
    },
    [t, namespace]
  );

  return { handleError };
}

interface UsePageErrorHandlerReturn {
  handleApiResponse: (
    error: unknown,
    setError: (error: string) => void
  ) => void;
}

/**
 * Hook for page-level error handling with consistent error state management
 */
export function usePageErrorHandler<_T = unknown>(
  namespace: string = 'common'
): UsePageErrorHandlerReturn {
  const { handleError } = useErrorHandler(namespace);

  const handleApiResponse = (
    error: unknown,
    setError: (error: string) => void
  ) => {
    const errorMessage = handleError(error);
    setError(errorMessage);
  };

  return { handleApiResponse };
}
